/**
 * PDF Overlay 座標轉換工具
 *
 * 後端座標系：inch，左上原點 (0,0)
 * PDF.js 渲染：px，左上原點（canvas.width = pageWidthPt * scale）
 *
 * 標準換算：
 *   1 inch = 72 pt（PDF 內部）
 *   以 CSS px 顯示時：px = inches * 72 * scale
 *
 * 注意：後端 PdfOverlayRenderer 在渲染時會套用全域 offset（OffsetXInches / OffsetYInches）
 * 及 BaselineLiftPoints。這些值由 renderSettings 傳入，讓編輯器顯示的方塊位置
 * 與最終 PDF 文字出現的位置一致。
 *
 * 術語：
 *   rawX/rawY  = op.x / op.y（儲存於 DB 的原始值，後端再加 offset 後渲染）
 *   dispX/dispY = rawX + offsetX, rawY + offsetY（視覺上文字出現的位置）
 */

export const PTS_PER_INCH = 72;

/** 預設 renderSettings（與 appsettings.json 相同的 fallback） */
export const DEFAULT_RENDER_SETTINGS = {
  offsetXInches: 0.0,
  offsetYInches: -0.05,
  baselineLiftPoints: 2.0,
};

/**
 * inch 座標 → canvas px
 */
export function inchesToPx(inches, scale) {
  return inches * PTS_PER_INCH * scale;
}

/**
 * canvas px → inch
 */
export function pxToInches(px, scale) {
  return px / (PTS_PER_INCH * scale);
}

/**
 * 將 FodgOperation 的原始 {x, y, w, h}（inch）轉換為 CSS absolute 定位（px）。
 * 套用 renderSettings offset 讓方塊顯示位置 = 最終 PDF 文字視覺位置。
 *
 * @param {object} op            FodgOperation（raw 座標）
 * @param {number} scale         PDF.js viewport scale
 * @param {object} [rs]          renderSettings（來自 overlay-edit API）
 * @returns {{ left, top, width, height }}  px
 */
export function opToRect(op, scale, rs) {
  const settings = rs ?? DEFAULT_RENDER_SETTINGS;
  // per-op offset 優先，否則用全域
  const effOffX = (op.offsetX != null ? op.offsetX : settings.offsetXInches) ?? 0;
  const effOffY = (op.offsetY != null ? op.offsetY : settings.offsetYInches) ?? 0;

  return {
    left:   inchesToPx((op.x ?? 0) + effOffX, scale),
    top:    inchesToPx((op.y ?? 0) + effOffY, scale),
    width:  inchesToPx(op.w ?? 1, scale),
    height: inchesToPx(op.h ?? 0.25, scale),
  };
}

/**
 * CSS absolute 定位 px（視覺位置，含 offset）→ FodgOperation 原始 {x, y, w, h} inch。
 * 拖動後用此函數將 display px 反算回 raw op.x / op.y 儲存。
 *
 * @param {{ left, top, width, height }} rect  視覺位置 px
 * @param {number} scale
 * @param {object} op    原始 op（需要其 offsetX/offsetY per-op 設定）
 * @param {object} [rs]  renderSettings
 * @returns {{ x, y, w, h }} inch（raw，適合寫回 op）
 */
export function rectToOpCoords(rect, scale, op, rs) {
  const settings = rs ?? DEFAULT_RENDER_SETTINGS;
  const effOffX = (op?.offsetX != null ? op.offsetX : settings.offsetXInches) ?? 0;
  const effOffY = (op?.offsetY != null ? op.offsetY : settings.offsetYInches) ?? 0;

  return {
    x: pxToInches(rect.left,   scale) - effOffX,
    y: pxToInches(rect.top,    scale) - effOffY,
    w: pxToInches(rect.width,  scale),
    h: pxToInches(rect.height, scale),
  };
}
